import { useState, useEffect, useMemo } from 'react';
import Chart from 'react-apexcharts';
import apiUser from '../../apiUser';
import LoadingSpinner from '../Loader/load';
import Modal from './Modal';
import { comVariacao } from './evolucaoCalc';

const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtComp = (c) => `${MESES[Number(String(c).slice(5, 7)) - 1]}/${String(c).slice(0, 4)}`;
const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);

const Evolucao = () => {
    const [serie, setSerie] = useState([]);
    const [filtros, setFiltros] = useState({ clientes: [], contratos: [] });
    const [cliente, setCliente] = useState('');
    const [contrato, setContrato] = useState('');
    const [loading, setLoading] = useState(true);
    const [gerando, setGerando] = useState(false);
    const [drill, setDrill] = useState(null); // { competencia, itens }

    const carregar = async (cli, con) => {
        setLoading(true);
        try {
            const params = {};
            if (cli) params.cliente = cli;
            if (con) params.contrato = con;
            const { data } = await apiUser.get('/faturamento/snapshots/evolucao', { params });
            if (data.success) setSerie(data.serie);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        apiUser.get('/faturamento/snapshots/filtros')
            .then(({ data }) => data.success && setFiltros({ clientes: data.clientes, contratos: data.contratos }))
            .catch(() => {});
        carregar('', '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const trocarCliente = (id) => { setCliente(id); setContrato(''); carregar(id, ''); };
    const trocarContrato = (id) => { setContrato(id); carregar(cliente, id); };

    const abrirMes = async (competencia) => {
        const params = { competencia };
        if (cliente) params.cliente = cliente;
        if (contrato) params.contrato = contrato;
        const { data } = await apiUser.get('/faturamento/snapshots/itens', { params });
        if (data.success) setDrill({ competencia, itens: data.itens });
    };

    const gerarAgora = async () => {
        setGerando(true);
        try {
            const { data } = await apiUser.post('/faturamento/snapshots/gerar');
            if (data.success) {
                alert(`Snapshot de ${fmtComp(data.competencia)} gerado: ${data.itens} itens em ${data.contratos} contratos${data.erros ? ` (${data.erros} erros — ver log)` : ''}.`);
                carregar(cliente, contrato);
            } else alert(data.message || 'Falha ao gerar snapshot.');
        } finally { setGerando(false); }
    };

    const linhas = useMemo(() => comVariacao(serie), [serie]);
    const contratosDoCliente = filtros.contratos.filter((c) => String(c.id_cliente) === String(cliente));

    const chart = {
        options: {
            chart: { id: 'evolucao-itens', toolbar: { show: false } },
            xaxis: { categories: linhas.map((l) => fmtComp(l.competencia)) },
            yaxis: { labels: { formatter: (v) => fmtBRL(v) } },
            stroke: { curve: 'smooth' },
            dataLabels: { enabled: false },
        },
        series: [{ name: 'Valor total', data: linhas.map((l) => Number(l.valor_total.toFixed(2))) }],
    };

    return (
        <div className='p-4'>
            <div className='flex flex-wrap items-end gap-3 mb-4'>
                <div>
                    <label className='block text-sm text-gray-600'>Cliente</label>
                    <select className='border rounded p-2' value={cliente} onChange={(e) => trocarCliente(e.target.value)}>
                        <option value=''>Todos (total geral)</option>
                        {filtros.clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                </div>
                {cliente && contratosDoCliente.length > 1 && (
                    <div>
                        <label className='block text-sm text-gray-600'>Contrato</label>
                        <select className='border rounded p-2' value={contrato} onChange={(e) => trocarContrato(e.target.value)}>
                            <option value=''>Todos do cliente</option>
                            {contratosDoCliente.map((c) => <option key={c.id} value={c.id}>{c.numero ? `${c.numero} — ` : ''}{c.nome || `Contrato ${c.id}`}</option>)}
                        </select>
                    </div>
                )}
                <button onClick={gerarAgora} disabled={gerando}
                    className='ml-auto bg-blue-600 text-white rounded px-3 py-2 disabled:opacity-50'>
                    {gerando ? 'Gerando…' : 'Gerar snapshot do mês atual'}
                </button>
            </div>

            {loading ? <LoadingSpinner /> : linhas.length === 0 ? (
                <p className='text-gray-500'>Nenhum snapshot ainda. O primeiro é gerado automaticamente no último dia do mês às 23h, ou agora pelo botão acima.</p>
            ) : (
                <>
                    <Chart options={chart.options} series={chart.series} type='line' height={280} />
                    <table className='w-full mt-4 text-sm'>
                        <thead><tr className='text-left border-b'>
                            <th className='py-2'>Competência</th><th>Itens</th><th>Quantidade</th><th>Valor total</th><th>Variação</th><th></th>
                        </tr></thead>
                        <tbody>
                            {linhas.map((l) => (
                                <tr key={l.competencia} className='border-b hover:bg-gray-50'>
                                    <td className='py-2'>{fmtComp(l.competencia)}</td>
                                    <td>{l.total_itens}</td>
                                    <td>{l.quantidade_total}</td>
                                    <td>{fmtBRL(l.valor_total)}</td>
                                    <td className={l.variacao > 0 ? 'text-green-700' : l.variacao < 0 ? 'text-red-700' : ''}>{fmtPct(l.variacao)}</td>
                                    <td><button className='text-blue-600 underline' onClick={() => abrirMes(l.competencia)}>ver itens</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {drill && (
                <Modal
                    title={`Itens do snapshot — ${fmtComp(drill.competencia)}`}
                    subtitle={`${drill.itens.length} itens`}
                    onClose={() => setDrill(null)}
                    footer={<button className='border rounded px-3 py-2' onClick={() => setDrill(null)}>Fechar</button>}
                >
                    <div className='px-6 py-3'>
                        <table className='w-full text-sm'>
                            <thead><tr className='text-left border-b'>
                                <th className='py-1'>Cliente</th><th>Descrição</th><th>Qtd</th><th>Ajustado</th><th>Unitário</th><th>Valor/mês</th>
                            </tr></thead>
                            <tbody>
                                {drill.itens.map((it) => (
                                    <tr key={it.id_item} className='border-b'>
                                        <td className='py-1'>{it.nome_cliente}</td>
                                        <td>{it.descricao}</td>
                                        <td>{it.quantidade}</td>
                                        <td>{Number(it.ajustado) ? 'sim' : ''}</td>
                                        <td>{fmtBRL(it.valor_unitario)}</td>
                                        <td>{fmtBRL(it.valor_mes)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Evolucao;
